import * as GoodsBlocks from './blocks'
import { addPortNode } from '@/port'
import {
  apiCommodityCommodityDetail,
  apiGroupBuyingOrderGroupOrderSkuInfo,
} from '@/api'
import { AsyncOrSync, OmitStrict } from 'vtils/types'
import { defineClassComponent } from '@/hooks'
import { getGoodsData, RawGoodsData } from './utils'
import { goodsProvider } from './provider'
import { h, onBeforeUnmount, onMounted, ref } from '@vue/composition-api'
import { showSkuPicker as showBookSkuPicker } from '@/views/book/goods/exports'
import { submit } from '@/utils'
import { wait } from 'vtils'

/**
 * 显示 SKU 选择器。
 */
export async function showSkuPicker(
  payload: OmitStrict<RawGoodsData['skuPicker'], 'visible'> & {
    goodsId: string
    availableSkuList?: () => AsyncOrSync<RawGoodsData['availableSkuList']>
    /** 初始 SKU ID */
    initialSkuId?: string
    /** 初始购买数量 */
    initialBuyCount?: number
  },
) {
  await submit(async $ => {
    $.start()
    const goods = await apiCommodityCommodityDetail({
      commodityId: payload.goodsId,
    })
    // 标销商品
    if (goods.commodityType === 1) {
      showBookSkuPicker({
        goodsId: payload.goodsId,
        isGroupBuying: payload.action === 'groupBuying',
        groupBuyingOrderId: payload.groupBuyingOrderId,
      })
      return
    }
    const availableSkuList: RawGoodsData['availableSkuList'] | undefined =
      payload.groupBuyingOrderId
        ? await apiGroupBuyingOrderGroupOrderSkuInfo({
            orderId: payload.groupBuyingOrderId,
          }).then(res =>
            res.skuInfos!.map(item => ({
              skuId: item.skuId!,
              groupBuyingPrice: item.maxPrice!,
            })),
          )
        : undefined
    const goodsData = (await getGoodsData({
      goodsId: payload.goodsId,
      availableSkuList: availableSkuList,
      simple: true,
      initialSkuId: payload.initialSkuId,
      initialBuyCount: payload.initialBuyCount,
    }))!
    if (goodsData.isOff) {
      goodsData.$destroy()
      return $.fail('商品已下架')
    }
    const SkuPicker = defineClassComponent(
      ({ ctx }) =>
        class {
          constructor() {
            goodsProvider.provide(this.goodsData)

            onMounted(() => {
              goodsData.showSkuPicker(payload)
              wait(300).then(() => {
                goodsData.$watch(
                  () => goodsData.skuPicker.visible,
                  () => {
                    if (!goodsData.skuPicker.visible) {
                      wait(500).then(() => {
                        ctx.$destroy()
                      })
                    }
                  },
                )
              })
            })

            onBeforeUnmount(() => {
              goodsData.$destroy()
              destroyPort()
            })
          }

          goodsData = ref(goodsData)

          render = () => h(GoodsBlocks.GoodsSkuPicker)
        },
    )
    const destroyPort = addPortNode(h(SkuPicker))
  })
}

/**
 * 统一处理商品相关接口的错误。
 */
export function handleGoodsError(actions: {
  商品下架?: () => void
  规格售罄?: () => void
  活动结束?: () => void
  活动不能参与?: () => void
}) {
  return (_err: any) => {
    const err = String(_err)
    if (err.includes('下架')) {
      actions.商品下架?.()
    } else if (err.includes('已售罄')) {
      actions.规格售罄?.()
    } else if (err.includes('已结束')) {
      actions.活动结束?.()
    } else if (err.includes('已满员') || err.includes('已过期')) {
      actions.活动不能参与?.()
    }
    throw err
  }
}
